import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const FinancialTab = () => {
  const { apiCall } = useAdminSettings();
  const [financialSettings, setFinancialSettings] = useState({});
  const [pendingPayouts, setPendingPayouts] = useState([]);

  const fetchFinancialSettings = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/financial');
      setFinancialSettings(data);
    } catch (error) {
      console.error('Error fetching financial settings:', error);
    }
  }, [apiCall]);

  const fetchPayouts = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/payouts');
      setPendingPayouts(data.pending_payouts || []);
    } catch (error) {
      console.error('Error fetching payouts:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchFinancialSettings();
    fetchPayouts();
  }, [fetchFinancialSettings, fetchPayouts]);

  return (
    <div className="space-y-6">
      {/* Earnings Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50">
          <CardContent className="p-6">
            <p className="text-gray-400 text-sm">Total Revenue</p>
            <p className="text-3xl font-bold text-green-400">
              ${financialSettings.earnings?.total_revenue?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/50">
          <CardContent className="p-6">
            <p className="text-gray-400 text-sm">Commission Earned</p>
            <p className="text-3xl font-bold text-purple-400">
              ${financialSettings.earnings?.total_commission?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/50">
          <CardContent className="p-6">
            <p className="text-gray-400 text-sm">Subscription Revenue</p>
            <p className="text-3xl font-bold text-blue-400">
              ${financialSettings.earnings?.subscription_revenue?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/50">
          <CardContent className="p-6">
            <p className="text-gray-400 text-sm">Total Transactions</p>
            <p className="text-3xl font-bold text-yellow-400">
              {financialSettings.earnings?.total_transactions || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Payouts */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Pending Payouts</span>
            <Button onClick={fetchPayouts} size="sm" variant="outline" className="border-gray-600">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingPayouts.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No pending payouts</p>
          ) : (
            <div className="space-y-3">
              {pendingPayouts.map((payout) => (
                <div key={payout.id || payout.email} className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{payout.username}</p>
                    <p className="text-sm text-gray-400">{payout.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-400">${payout.balance.toFixed(2)}</p>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white mt-1">
                      Process
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialTab;
